'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import I18n from 'I18n';
import CodeAccordionItem from 'DesignEditorsUI/components/customWidgetEditor/codePanes/CodeAccordionItem';
import Pane from 'DesignComponents/resizablePanes/Pane';
import PaneSize from 'DesignData/models/PaneSize';
import CodeEditorError from 'DesignData/models/validation/CodeEditorError';
import { MIN_PANE_SIZE } from 'DesignData/constants/PaneSizes';
import { PIXELS } from 'DesignData/enums/SizeUnits';
import { HORIZONTAL, VERTICAL } from 'DesignData/enums/AxisDirections';
import { HTML, CSS, JS } from 'DesignData/enums/CustomWidgetEditorPaneTypes';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { getUserHasReadAccessOnly } from 'DesignData/selectors/auth';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as editorErrorSelectors from 'DesignData/selectors/editorErrors';
import { getIsProjectsFile } from 'DesignData/selectors/finder';
const EMPTY_WIDGET_CONTEXT = EMPTY_OBJECT;

const makeMapStateToProps = () => {
  const getSourceErrors = editorErrorSelectors.makeGetSourceErrors();
  const getFieldsContext = customWidgetBuffersSelectors.makeGetFieldsContext();
  const getIsReadOnly = customWidgetBuffersSelectors.makeGetIsReadOnly();
  return (state, ownProps) => {
    return {
      source: customWidgetBuffersSelectors.getCustomWidgetBufferSource(state, ownProps),
      isReadOnly: getUserHasReadAccessOnly(state) || getIsReadOnly(state, ownProps),
      isProjectsFile: getIsProjectsFile(state, {
        fileId: ownProps.customWidgetId
      }),
      sourceErrors: getSourceErrors(state, ownProps),
      customWidgetContext: ownProps.enableHublValidation ? getFieldsContext(state, ownProps) : EMPTY_WIDGET_CONTEXT,

      /* Pane props from cloneElement */
      paneDirection: ownProps.direction,
      paneSize: ownProps.size,
      paneKey: ownProps.key
    };
  };
};

class ConnectedCodePane extends Component {
  componentDidMount() {
    this.props.onCodePanesRender();
  }

  componentDidUpdate(prevProps) {
    if (this.props.customWidgetId !== prevProps.customWidgetId) {
      this.props.onCodePanesRender();
    }
  }

  get labels() {
    if (!this.__labels) {
      this.__labels = {
        [HTML]: `module.html (${I18n.text('DesignEditorsUI.codeFileTypes.hubl-html')})`,
        [CSS]: 'module.css',
        [JS]: 'module.js'
      };
    }

    return this.__labels;
  }

  render() {
    const {
      paneOptions,
      paneId,
      customWidgetId,
      revisionId,
      sessionId,
      mode,
      source,
      enableHublValidation,
      autofocus,
      refresh,
      customWidgetContext,
      isReadOnly,
      isProjectsFile,
      onCodeChange,
      onOpenChange,
      sourceErrors,
      onHublValidation,
      onHublValidationFail,
      paneDirection,
      paneSize,
      paneKey
    } = this.props;
    const {
      size,
      open
    } = paneOptions[paneId] || {};
    const defaultSize = open === false ? new PaneSize({
      value: MIN_PANE_SIZE,
      units: PIXELS
    }) : size;
    return /*#__PURE__*/_jsx(Pane, {
      className: "code-pane",
      defaultSize: defaultSize,
      forceChildRerender: true,
      paneId: paneId,
      direction: paneDirection,
      size: paneSize,
      children: /*#__PURE__*/_jsx(CodeAccordionItem, {
        sessionId: sessionId,
        label: this.labels[paneId],
        isReadOnly: isReadOnly,
        isProjectsFile: isProjectsFile,
        mode: mode,
        onCodeChange: onCodeChange,
        onOpenChange: onOpenChange,
        open: open,
        errors: sourceErrors,
        value: source,
        enableHublValidation: enableHublValidation,
        onHublValidation: onHublValidation,
        onHublValidationFail: onHublValidationFail,
        autofocus: autofocus,
        refresh: refresh,
        customWidgetContext: customWidgetContext
      }, `${customWidgetId}:${revisionId}:${paneId}`)
    }, paneKey);
  }

}

ConnectedCodePane.propTypes = {
  paneId: PropTypes.oneOf([HTML, CSS, JS]).isRequired,
  mode: PropTypes.string.isRequired,
  enableHublValidation: PropTypes.bool.isRequired,
  autofocus: PropTypes.bool.isRequired,
  sessionId: PropTypes.string.isRequired,
  paneOptions: PropTypes.object.isRequired,
  refresh: PropTypes.bool.isRequired,
  onCodeChange: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onHublValidation: PropTypes.func.isRequired,
  onHublValidationFail: PropTypes.func.isRequired,
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  revisionId: PropTypes.number,
  onCodePanesRender: PropTypes.func.isRequired,

  /* Connect */
  isReadOnly: PropTypes.bool.isRequired,
  isProjectsFile: PropTypes.bool,
  sourceErrors: PropTypes.arrayOf(PropTypes.instanceOf(CodeEditorError)).isRequired,
  source: PropTypes.string.isRequired,
  customWidgetContext: PropTypes.object.isRequired,

  /* Pane props */
  paneDirection: PropTypes.oneOf([HORIZONTAL, VERTICAL]),
  paneSize: PropTypes.instanceOf(PaneSize),
  paneKey: PropTypes.string
};
ConnectedCodePane.defaultProps = {
  enableHublValidation: false,
  autofocus: false,
  paneOptions: {},
  onCodeChange: emptyFunction
};
export default connect(makeMapStateToProps, null, null, {
  forwardRef: true
})(ConnectedCodePane);